import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDateRelative } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Sparkles, Mail, Target, MapPin, Briefcase, Users, History, Copy, Check, Shield, AlertTriangle, Brain, TrendingUp, Pencil } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import FundingPanel from "../components/FundingPanel";
import { motion } from "framer-motion";
import { toast } from "sonner";

const TYPE_LABEL = {
    buyer: "Buyer",
    seller: "Seller",
    intermediary: "Intermediary",
    investor: "Investor",
};

const TRACKING_LABEL = {
    not_contacted: "Not contacted",
    teaser_sent: "Teaser sent",
    nda_signed: "NDA signed",
    cim_shared: "CIM shared",
    ioi_received: "IOI received",
    dropped: "Dropped",
};

const IOI_BADGE = {
    active: "bg-gold/15 text-gold border-gold/40",
    accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    withdrawn: "bg-neutral-100 text-neutral-600 border-neutral-300",
};

const DEAL_TYPE_DISPLAY = {
    asset_purchase: "Asset Purchase",
    share_purchase: "Share Purchase",
    mbo: "MBO",
    mbi: "MBI",
    merger: "Merger",
};
const DEAL_TYPE_OPTS = ["asset_purchase", "share_purchase", "mbo", "mbi", "merger"];

const ACQ_FREQ_BADGE = {
    very_active: "bg-navy/15 text-navy border-navy/40",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    occasional: "bg-amber-50 text-amber-700 border-amber-200",
    rare: "bg-neutral-100 text-neutral-600 border-neutral-300",
};
const ACQ_FREQ_LABEL = {
    very_active: "Very active (3+ /yr)",
    active: "Active (1–2 /yr)",
    occasional: "Occasional (<1 /yr)",
    rare: "Rare",
};
const REL_STRENGTH_BADGE = {
    cold: "bg-neutral-100 text-neutral-600 border-neutral-300",
    warm: "bg-amber-50 text-amber-700 border-amber-200",
    strong: "bg-emerald-50 text-emerald-700 border-emerald-200",
    strategic_partner: "bg-navy/15 text-navy border-navy/40",
};
const REL_STRENGTH_LABEL = {
    cold: "Cold",
    warm: "Warm",
    strong: "Strong",
    strategic_partner: "Strategic Partner",
};

export default function BuyerProfile() {
    const { id } = useParams();
    const nav = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [intel, setIntel] = useState(null);
    const [intelLoading, setIntelLoading] = useState(false);
    const [intelEditOpen, setIntelEditOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    const refresh = async () => {
        try {
            const { data } = await api.get(`/contacts/${id}/profile`);
            setProfile(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not load profile");
            nav("/contacts");
        }
    };
    const fetchIntel = async () => {
        setIntelLoading(true);
        try {
            const { data } = await api.get(`/contacts/${id}/intelligence-summary`);
            setIntel(data);
        } catch {
            // non-critical — tab renders with empty state
        } finally {
            setIntelLoading(false);
        }
    };
    const [timeline, setTimeline] = useState(null);
    const [timelineLoading, setTimelineLoading] = useState(false);
    const fetchTimeline = async () => {
        setTimelineLoading(true);
        try {
            const { data } = await api.get(`/contacts/${id}/timeline`);
            setTimeline(data);
        } catch {
            setTimeline({ events: [], total_events: 0 });
        } finally {
            setTimelineLoading(false);
        }
    };
    useEffect(() => {
        refresh().finally(() => setLoading(false));
        fetchIntel();
        fetchTimeline();
    }, [id]);

    if (loading) {
        return <div className="p-8 text-sm text-muted-foreground">Loading buyer profile…</div>;
    }
    if (!profile) return null;

    const c = profile.contact;
    const isBuyer = c.type === "buyer";

    return (
        <div data-testid="buyer-profile-page">
            <PageHeader
                title={c.name}
                subtitle={[
                    c.company && c.company,
                    TYPE_LABEL[c.type] || c.type,
                    c.role,
                    c.pe_backed ? "PE-backed" : null,
                ].filter(Boolean).join(" · ")}
            >
                <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-contact-btn">
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit contact
                </Button>
                <Button asChild variant="outline" size="sm" data-testid="back-to-contacts-btn">
                    <Link to="/contacts"><ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to contacts</Link>
                </Button>
            </PageHeader>

            <EditContactDialog
                contact={profile.contact}
                open={editOpen}
                onOpenChange={setEditOpen}
                onDone={() => { setEditOpen(false); refresh(); }}
            />

            <div className="p-6 lg:p-8 space-y-6 max-w-6xl">
                {(c.type === "buyer" || c.type === "investor") && (
                    <FundingPanel contact={c} onSaved={refresh} />
                )}

                {/* Stats strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatTile label="Active deals" value={profile.stats.active_deals_count} testid="stat-active-deals" />
                    <StatTile label="IOIs (lifetime)" value={profile.stats.total_iois} testid="stat-total-iois" />
                    <StatTile label="Active IOI value" value={fmtMoney(profile.stats.active_ioi_value, "GBP")} mono testid="stat-active-ioi-value" />
                    <StatTile label="Past acquisitions" value={profile.stats.past_acquisitions_count} testid="stat-past-acq" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left rail - mandate / criteria */}
                    <div className="lg:col-span-1 space-y-4">
                        <div className="border border-border rounded-md bg-white p-5">
                            <h3 className="font-display text-sm font-bold tracking-tight uppercase text-muted-foreground mb-3 flex items-center gap-1.5">
                                <Target className="w-3.5 h-3.5 text-gold" /> Investment criteria
                            </h3>
                            <CriteriaRow icon={<Briefcase className="w-3.5 h-3.5" />} label="Sectors" values={c.mandate_sectors} />
                            <CriteriaRow icon={<MapPin className="w-3.5 h-3.5" />} label="Geographies" values={c.mandate_geographies} />
                            <RangeRow label="EBITDA band" lo={c.mandate_ebitda_min} hi={c.mandate_ebitda_max} />
                            <RangeRow label="Ticket size" lo={c.ticket_size_min} hi={c.ticket_size_max} accent />
                            {c.tags?.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-border/60">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Tags</div>
                                    <div className="flex flex-wrap gap-1">
                                        {c.tags.map((t, i) => (
                                            <span key={i} className="text-[10px] px-1.5 py-0.5 border border-border rounded-sm bg-muted/30 font-mono">{t}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="border border-border rounded-md bg-white p-5">
                            <h3 className="font-display text-sm font-bold tracking-tight uppercase text-muted-foreground mb-3 flex items-center gap-1.5">
                                <Users className="w-3.5 h-3.5 text-gold" /> Relationship
                            </h3>
                            <KV label="Owner" value={c.relationship_owner || "—"} />
                            <KV label="Email" value={c.email || "—"} mono />
                            <KV label="Phone" value={c.phone || "—"} mono />
                            <KV label="Created" value={fmtDateRelative(c.created_at)} />
                        </div>

                        <KycCard contactId={c.id} contactName={c.name} />

                        {c.past_acquisitions?.length > 0 && (
                            <div className="border border-border rounded-md bg-white p-5">
                                <h3 className="font-display text-sm font-bold tracking-tight uppercase text-muted-foreground mb-3 flex items-center gap-1.5">
                                    <History className="w-3.5 h-3.5 text-gold" /> Past acquisitions
                                </h3>
                                <ul className="text-sm space-y-1.5">
                                    {c.past_acquisitions.map((p, i) => (
                                        <li key={i} className="font-mono text-xs text-foreground border-l-2 border-gold/40 pl-2">{p}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {c.notes && (
                            <div className="border border-border rounded-md bg-white p-5">
                                <h3 className="font-display text-sm font-bold tracking-tight uppercase text-muted-foreground mb-3">Internal notes</h3>
                                <p className="text-sm whitespace-pre-wrap text-foreground/90">{c.notes}</p>
                            </div>
                        )}
                    </div>

                    {/* Right - tabs */}
                    <div className="lg:col-span-2">
                        <Tabs defaultValue="active">
                            <TabsList data-testid="profile-tabs">
                                <TabsTrigger value="active" data-testid="tab-active-deals">Active deals ({profile.active_deals.length})</TabsTrigger>
                                <TabsTrigger value="iois" data-testid="tab-iois">IOI history ({profile.ioi_history.length})</TabsTrigger>
                                {isBuyer && <TabsTrigger value="suggested" data-testid="tab-suggested">Suggested matches ({profile.suggested_deals.length})</TabsTrigger>}
                                {isBuyer && <TabsTrigger value="intelligence" data-testid="tab-intelligence">Intelligence</TabsTrigger>}
                                <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
                            </TabsList>

                            <TabsContent value="active" className="mt-4">
                                <DealList
                                    deals={profile.active_deals}
                                    emptyMsg="This contact is not linked to any active deals yet."
                                    showStatus
                                    testid="active-deals-list"
                                    contact={c}
                                    isBuyer={isBuyer}
                                />
                            </TabsContent>

                            <TabsContent value="iois" className="mt-4">
                                <IOIList iois={profile.ioi_history} />
                            </TabsContent>

                            {isBuyer && (
                                <TabsContent value="suggested" className="mt-4">
                                    <SuggestedDeals deals={profile.suggested_deals} contact={c} />
                                </TabsContent>
                            )}
                            {isBuyer && (
                                <TabsContent value="intelligence" className="mt-4">
                                    <IntelligenceTab
                                        contact={c}
                                        intel={intel}
                                        loading={intelLoading}
                                        onEdit={() => setIntelEditOpen(true)}
                                    />
                                    <IntelligenceEditDialog
                                        contact={c}
                                        intel={intel}
                                        open={intelEditOpen}
                                        onOpenChange={setIntelEditOpen}
                                        onDone={() => { setIntelEditOpen(false); fetchIntel(); refresh(); }}
                                    />
                                </TabsContent>
                            )}
                            <TabsContent value="timeline" className="mt-4" data-testid="timeline-tab-content">
                                <RelationshipTimeline
                                    timeline={timeline}
                                    loading={timelineLoading}
                                    contactType={c.type}
                                />
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Relationship Timeline component ─────────────────────────────────────────

const TIMELINE_LABEL = {
    "buyer.added_to_pipeline": "Added to pipeline",
    "buyer.status_changed": "Status updated",
    "ioi.submitted": "IOI submitted",
    "deal.created": "Deal linked",
    "deal.stage_changed": "Stage progressed",
    "deal.closed_won": "Deal completed",
    "deal.closed_lost": "Deal lost",
};
const TIMELINE_DOT = {
    "buyer.added_to_pipeline": "bg-blue-500",
    "buyer.status_changed":    "bg-amber-500",
    "ioi.submitted":           "bg-yellow-500",
    "deal.created":            "bg-neutral-400",
    "deal.stage_changed":      "bg-neutral-400",
    "deal.closed_won":         "bg-emerald-500",
    "deal.closed_lost":        "bg-red-400",
};

function TimelineEventDetail({ event }) {
    const d = event.detail || {};
    if (event.event_type === "ioi.submitted") {
        const val = d.value != null ? new Intl.NumberFormat("en-GB", { style: "currency", currency: d.currency || "GBP", maximumFractionDigits: 0 }).format(d.value) : null;
        return <span className="text-muted-foreground">{val ? `${val} · ` : ""}{d.status || ""}</span>;
    }
    if (event.event_type === "buyer.status_changed") return <span className="text-muted-foreground capitalize">{(d.status || "").replace(/_/g, " ")}</span>;
    if (event.event_type === "deal.stage_changed") return <span className="text-muted-foreground">{d.from_stage} → {d.to_stage}</span>;
    if (event.event_type === "deal.created" || event.event_type === "deal.closed_won" || event.event_type === "deal.closed_lost") {
        const size = d.deal_size != null ? new Intl.NumberFormat("en-GB", { style: "currency", currency: d.currency || "GBP", maximumFractionDigits: 0 }).format(d.deal_size) : null;
        return <span className="text-muted-foreground">{size || ""}</span>;
    }
    return null;
}

function RelationshipTimeline({ timeline, loading, contactType }) {
    if (loading) return <div className="text-sm text-muted-foreground py-4">Loading timeline…</div>;
    const events = timeline?.events || [];
    if (events.length === 0) return (
        <div className="border border-border rounded-md p-6 text-center text-sm text-muted-foreground" data-testid="timeline-empty">
            <div className="font-medium mb-1">No relationship history yet</div>
            <div className="text-xs">
                {contactType === "buyer" || contactType === "investor"
                    ? "Events will appear here when this contact is added to deal pipelines."
                    : "Events will appear here when deals are linked to this contact."}
            </div>
        </div>
    );
    return (
        <div className="relative" data-testid="relationship-timeline">
            <div className="absolute left-3.5 top-2 bottom-2 w-px bg-border" />
            <div className="space-y-4">
                {events.map((ev, i) => (
                    <div key={i} className="flex items-start gap-4 relative" data-testid="timeline-event">
                        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 z-10 ${TIMELINE_DOT[ev.event_type] || "bg-neutral-400"}`} />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <span className="text-sm font-medium text-foreground">
                                        {TIMELINE_LABEL[ev.event_type] || ev.event_type}
                                    </span>
                                    {" "}
                                    <span className="text-sm text-foreground/80 font-medium">{ev.deal_name}</span>
                                    {" "}
                                    <TimelineEventDetail event={ev} />
                                </div>
                                <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                                    {ev.occurred_at ? new Date(ev.occurred_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function StatTile({ label, value, mono, testid }) {
    return (
        <div className="border border-border rounded-md bg-white p-4" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`text-2xl font-bold ${mono ? "font-mono text-navy" : "font-display"} mt-1`}>{value ?? "—"}</div>
        </div>
    );
}

function CriteriaRow({ icon, label, values }) {
    if (!values?.length) return (
        <div className="text-xs text-muted-foreground py-1.5 flex items-center gap-1.5">
            <span className="text-muted-foreground/60">{icon}</span> {label}: <span className="italic">not set</span>
        </div>
    );
    return (
        <div className="py-1.5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-1">
                {icon} {label}
            </div>
            <div className="flex flex-wrap gap-1">
                {values.map((v, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 bg-muted/40 border border-border rounded-sm">{v}</span>
                ))}
            </div>
        </div>
    );
}

function RangeRow({ label, lo, hi, accent }) {
    if (lo == null && hi == null) return null;
    const fmt = (v) => v == null ? "∞" : new Intl.NumberFormat().format(v);
    return (
        <div className="py-1.5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
            <div className={`font-mono text-sm ${accent ? "text-gold font-semibold" : "text-navy"}`}>
                {fmt(lo)} <span className="text-muted-foreground/60">–</span> {fmt(hi)}
            </div>
        </div>
    );
}

function KV({ label, value, mono }) {
    return (
        <div className="flex items-center justify-between py-1.5 border-b border-border/40 last:border-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`text-xs ${mono ? "font-mono" : ""} text-foreground/90 truncate ml-2`}>{value}</div>
        </div>
    );
}

function DealList({ deals, emptyMsg, showStatus, testid, contact, isBuyer }) {
    if (!deals.length) {
        return (
            <div className="border border-dashed border-border rounded-md p-10 text-center text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
                {emptyMsg}
            </div>
        );
    }
    return (
        <div className="border border-border rounded-md bg-white overflow-hidden" data-testid={testid}>
            {deals.map((d, i) => (
                <motion.div
                    key={d.deal_id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.2), duration: 0.2 }}
                    className="border-b border-border/60 last:border-0 p-4 hover:bg-muted/30 flex items-center justify-between gap-3"
                    data-testid={`active-deal-${d.deal_id}`}
                >
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <Link to={`/deals/${d.deal_id}`} className="font-medium hover:text-gold transition-colors">
                                {d.company_name}
                            </Link>
                            <Badge variant="outline" className="text-[10px] capitalize">{d.stage}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground font-mono mt-0.5">
                            {d.sector || "—"} · {fmtMoney(d.deal_size, d.currency)}
                            {showStatus && d.tracking_status && (
                                <> · <span className="text-foreground/80">{TRACKING_LABEL[d.tracking_status] || d.tracking_status}</span></>
                            )}
                        </div>
                    </div>
                    {isBuyer && (
                        <BuyerFitDialog contact={contact} deal={{ id: d.deal_id, company_name: d.company_name }} />
                    )}
                </motion.div>
            ))}
        </div>
    );
}

function IOIList({ iois }) {
    if (!iois.length) {
        return (
            <div className="border border-dashed border-border rounded-md p-10 text-center text-sm text-muted-foreground" data-testid="ioi-history-empty">
                No IOIs received from this contact yet.
            </div>
        );
    }
    return (
        <div className="border border-border rounded-md bg-white overflow-hidden" data-testid="ioi-history-list">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                        <th className="text-left px-4 py-2.5">Deal</th>
                        <th className="text-right px-4 py-2.5">Value</th>
                        <th className="text-left px-4 py-2.5">Status</th>
                        <th className="text-left px-4 py-2.5">Received</th>
                    </tr>
                </thead>
                <tbody>
                    {iois.map((i) => (
                        <tr key={i.id} className="border-b border-border/60 last:border-0 hover:bg-muted/30">
                            <td className="px-4 py-3">
                                <Link to={`/deals/${i.deal_id}`} className="font-medium hover:text-gold transition-colors">
                                    {i.deal_name || "—"}
                                </Link>
                                {i.notes && <div className="text-xs text-muted-foreground truncate max-w-md mt-0.5">{i.notes}</div>}
                            </td>
                            <td className="px-4 py-3 text-right font-mono font-medium text-navy">{fmtMoney(i.value, i.currency)}</td>
                            <td className="px-4 py-3">
                                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border font-medium ${IOI_BADGE[i.status] || ""}`}>
                                    {i.status}
                                </span>
                            </td>
                            <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                                {i.received_at ? new Date(i.received_at).toLocaleDateString() : "—"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SuggestedDeals({ deals, contact }) {
    if (!deals.length) {
        return (
            <div className="border border-dashed border-border rounded-md p-10 text-center text-sm text-muted-foreground" data-testid="suggested-deals-empty">
                No suggested deals match this buyer's criteria yet. Add more deals or refine the buyer's mandate.
            </div>
        );
    }
    return (
        <div className="space-y-3" data-testid="suggested-deals-list">
            {deals.map((d, i) => (
                <motion.div
                    key={d.deal_id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.04, 0.3), duration: 0.25 }}
                    className="border border-border rounded-md bg-white p-4 hover:border-gold/60 transition-colors"
                    data-testid={`suggested-deal-${d.deal_id}`}
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <Link to={`/deals/${d.deal_id}`} className="font-medium hover:text-gold transition-colors">
                                    {d.company_name}
                                </Link>
                                <Badge variant="outline" className="text-[10px] capitalize">{d.stage}</Badge>
                                <span className="ml-auto text-xs font-mono font-bold text-gold" data-testid={`match-score-${d.deal_id}`}>
                                    {d.score_pct}% match
                                </span>
                            </div>
                            <div className="text-xs text-muted-foreground font-mono mt-1">
                                {d.sector || "—"} · {fmtMoney(d.deal_size, d.currency)}
                            </div>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {d.reasons.map((r, idx) => (
                                    <span key={idx} className="text-[10px] px-2 py-0.5 bg-gold/10 text-gold border border-gold/30 rounded-sm">
                                        {r}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <BuyerFitDialog contact={contact} deal={{ id: d.deal_id, company_name: d.company_name }} />
                    </div>
                </motion.div>
            ))}
        </div>
    );
}

function BuyerFitDialog({ contact, deal }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    const run = async () => {
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.post("/ai/buyer-fit", { contact_id: contact.id, deal_id: deal.id });
            setResult(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "AI analysis failed");
        } finally {
            setLoading(false);
        }
    };

    const copyOutreach = async () => {
        if (!result?.suggested_outreach) return;
        try {
            await navigator.clipboard.writeText(result.suggested_outreach);
            setCopied(true);
            toast.success("Outreach copied");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Could not copy");
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v && !result && !loading) run(); }}>
            <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="gap-1.5 shrink-0" data-testid={`ai-fit-btn-${deal.id}`}>
                    <Sparkles className="w-3.5 h-3.5 text-gold" /> AI fit
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-fit-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-gold" /> Buyer fit · {deal.company_name}
                    </DialogTitle>
                </DialogHeader>

                {loading && (
                    <div className="py-12 text-center text-sm text-muted-foreground" data-testid="ai-fit-loading">
                        <Sparkles className="w-5 h-5 text-gold mx-auto mb-2 animate-pulse" />
                        Analysing buyer fit with Claude Sonnet 4.5…
                    </div>
                )}

                {result && (
                    <div className="space-y-4" data-testid="ai-fit-result">
                        {/* Likelihood gauge */}
                        <div className="border border-border rounded-md bg-white p-4">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Likelihood to bid</div>
                                <div className="font-mono text-2xl font-bold text-gold" data-testid="ai-fit-likelihood">
                                    {result.likelihood_to_bid}%
                                </div>
                            </div>
                            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${result.likelihood_to_bid}%` }}
                                    transition={{ duration: 0.6, ease: "easeOut" }}
                                    className="h-full bg-gradient-to-r from-gold to-gold/70"
                                />
                            </div>
                            {result.likelihood_rationale && (
                                <p className="text-xs text-muted-foreground mt-2 italic">{result.likelihood_rationale}</p>
                            )}
                        </div>

                        {/* Why match */}
                        <div className="border border-border rounded-md bg-white p-4">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                                <Target className="w-3 h-3 text-gold" /> Why this buyer matches
                            </div>
                            <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap" data-testid="ai-fit-why">
                                {result.why_match}
                            </p>
                        </div>

                        {/* Concerns */}
                        {result.key_concerns?.length > 0 && (
                            <div className="border border-amber-200 bg-amber-50/40 rounded-md p-4">
                                <div className="text-[10px] uppercase tracking-wider text-amber-800 mb-2">Key concerns</div>
                                <ul className="text-sm space-y-1 list-disc pl-4 text-foreground/90">
                                    {result.key_concerns.map((k, i) => <li key={i}>{k}</li>)}
                                </ul>
                            </div>
                        )}

                        {/* Suggested outreach */}
                        {result.suggested_outreach && (
                            <div className="border border-border rounded-md bg-white p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                        <Mail className="w-3 h-3 text-gold" /> Suggested outreach
                                    </div>
                                    <Button size="sm" variant="ghost" onClick={copyOutreach} className="h-7 gap-1.5" data-testid="ai-fit-copy-outreach">
                                        {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                                        {copied ? "Copied" : "Copy"}
                                    </Button>
                                </div>
                                <pre className="text-xs leading-relaxed text-foreground/90 whitespace-pre-wrap font-sans bg-muted/30 border border-border/40 rounded-sm p-3 overflow-x-auto">
                                    {result.suggested_outreach}
                                </pre>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" size="sm" onClick={run} disabled={loading} data-testid="ai-fit-regenerate">
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {result ? "Regenerate" : "Analyse"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}



const RISK_OPTS = ["unrated", "low", "medium", "high", "prohibited"];
const STATUS_OPTS = ["not_started", "pending", "complete", "expired"];
const KYC_RISK_BADGE = {
    low: "bg-emerald-50 text-emerald-700 border-emerald-200",
    medium: "bg-amber-50 text-amber-700 border-amber-200",
    high: "bg-orange-50 text-orange-700 border-orange-300",
    prohibited: "bg-red-50 text-red-700 border-red-200",
    unrated: "bg-neutral-100 text-neutral-600 border-neutral-300",
};
const KYC_STATUS_BADGE = {
    complete: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    expired: "bg-red-50 text-red-700 border-red-200",
    not_started: "bg-neutral-100 text-neutral-600 border-neutral-300",
};

function KycCard({ contactId, contactName }) {
    const [rec, setRec] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);

    const refresh = async () => {
        try {
            const { data } = await api.get(`/compliance/cdd/contact/${contactId}`);
            setRec(data);
        } catch (err) {
            if (err.response?.status === 404) setRec(null);
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, [contactId]);

    if (loading) return null;

    const status = rec?.cdd_status || "not_started";
    const risk = rec?.risk_rating || "unrated";
    const expired = rec?.cdd_expiry_date && new Date(rec.cdd_expiry_date) < new Date();

    return (
        <div className="border border-border rounded-md bg-white p-5" data-testid="kyc-card">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-sm font-bold tracking-tight uppercase text-muted-foreground flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5 text-gold" /> KYC / CDD
                </h3>
                <Dialog open={editOpen} onOpenChange={setEditOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="kyc-edit-btn">
                            {rec ? "Edit" : "Start CDD"}
                        </Button>
                    </DialogTrigger>
                    <KycDialog
                        contactId={contactId}
                        contactName={contactName}
                        existing={rec}
                        onDone={() => { setEditOpen(false); refresh(); }}
                    />
                </Dialog>
            </div>
            {!rec ? (
                <p className="text-xs text-muted-foreground">No CDD record yet for this contact.</p>
            ) : (
                <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border font-medium ${KYC_RISK_BADGE[risk] || ""}`}>{risk}</span>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border font-medium ${KYC_STATUS_BADGE[status] || ""}`}>{status}</span>
                        {expired && <span className="text-[10px] uppercase tracking-wider text-red-700 font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> EXPIRED</span>}
                    </div>
                    <div className="font-mono text-muted-foreground space-y-1 mt-2">
                        <div>ID verified: {rec.id_verified ? "✓" : "—"}</div>
                        <div>Source of funds: {rec.source_of_funds_verified ? "✓" : "—"}</div>
                        <div>PEP: {rec.pep_checked ? (rec.pep_result === "match" ? "MATCH" : "✓") : "—"}</div>
                        <div>Sanctions: {rec.sanctions_checked ? (rec.sanctions_result === "match" ? "MATCH" : "✓") : "—"}</div>
                        {rec.enhanced_due_diligence && <div className="text-orange-700">EDD applied</div>}
                        {rec.cdd_expiry_date && <div>Expires {new Date(rec.cdd_expiry_date).toLocaleDateString()}</div>}
                    </div>
                </div>
            )}
        </div>
    );
}

function KycDialog({ contactId, contactName, existing, onDone }) {
    const [form, setForm] = useState({
        risk_rating: existing?.risk_rating || "unrated",
        cdd_status: existing?.cdd_status || "not_started",
        id_verified: existing?.id_verified || false,
        id_document_type: existing?.id_document_type || "",
        source_of_funds_verified: existing?.source_of_funds_verified || false,
        pep_checked: existing?.pep_checked || false,
        pep_result: existing?.pep_result || "",
        sanctions_checked: existing?.sanctions_checked || false,
        sanctions_result: existing?.sanctions_result || "",
        enhanced_due_diligence: existing?.enhanced_due_diligence || false,
        edd_reason: existing?.edd_reason || "",
        cdd_date: existing?.cdd_date || new Date().toISOString().slice(0, 10),
        cdd_expiry_date: existing?.cdd_expiry_date || "",
        notes: existing?.notes || "",
    });
    const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? (v.target.type === "checkbox" ? v.target.checked : v.target.value) : v }));
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = { ...form };
            if (!existing) payload.contact_id = contactId;
            if (existing) await api.patch(`/compliance/cdd/${existing.id}`, payload);
            else await api.post("/compliance/cdd", payload);
            toast.success(existing ? "CDD updated" : "CDD record created");
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally { setLoading(false); }
    };

    return (
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="kyc-dialog">
            <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                    <Shield className="w-4 h-4 text-gold" /> CDD · {contactName}
                </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">Risk rating</label>
                        <Select value={form.risk_rating} onValueChange={setF("risk_rating")}>
                            <SelectTrigger data-testid="kyc-risk-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{RISK_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">Status</label>
                        <Select value={form.cdd_status} onValueChange={setF("cdd_status")}>
                            <SelectTrigger data-testid="kyc-status-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUS_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
                <CheckRow checked={form.id_verified} onChange={setF("id_verified")} label="ID verified" testid="kyc-id-verified" />
                <CheckRow checked={form.source_of_funds_verified} onChange={setF("source_of_funds_verified")} label="Source of funds verified" testid="kyc-sof-verified" />
                <CheckRow checked={form.pep_checked} onChange={setF("pep_checked")} label="PEP screening complete" testid="kyc-pep-checked" />
                <CheckRow checked={form.sanctions_checked} onChange={setF("sanctions_checked")} label="Sanctions screening complete" testid="kyc-sanctions-checked" />
                <CheckRow checked={form.enhanced_due_diligence} onChange={setF("enhanced_due_diligence")} label="Enhanced Due Diligence (EDD)" testid="kyc-edd" />
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">CDD date</label>
                        <input type="date" className="w-full h-9 border border-border rounded-md px-2 text-sm" value={form.cdd_date} onChange={setF("cdd_date")} data-testid="kyc-cdd-date" />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">Expires</label>
                        <input type="date" className="w-full h-9 border border-border rounded-md px-2 text-sm" value={form.cdd_expiry_date} onChange={setF("cdd_expiry_date")} data-testid="kyc-cdd-expiry" />
                    </div>
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</label>
                    <textarea rows={2} className="w-full border border-border rounded-md p-2 text-sm" value={form.notes} onChange={setF("notes")} />
                </div>
                <DialogFooter>
                    <Button type="submit" disabled={loading} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="kyc-submit-btn">
                        {loading ? "Saving…" : "Save CDD"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}

function CheckRow({ checked, onChange, label, testid }) {
    return (
        <label className="flex items-center gap-2 border border-border/60 rounded-md p-2.5 cursor-pointer text-sm">
            <input type="checkbox" checked={checked} onChange={onChange} className="accent-navy" data-testid={testid} />
            {label}
        </label>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Edit Contact Dialog
// ─────────────────────────────────────────────────────────────────────────────

const CONTACT_TYPES = [
    { v: "buyer", label: "Buyer" },
    { v: "seller", label: "Seller" },
    { v: "intermediary", label: "Intermediary" },
    { v: "investor", label: "Investor" },
];

function EditContactDialog({ contact: c, open, onOpenChange, onDone }) {
    const [form, setForm] = useState({
        name: c.name || "",
        type: c.type || "buyer",
        email: c.email || "",
        phone: c.phone || "",
        company: c.company || "",
        role: c.role || "",
        relationship_owner: c.relationship_owner || "",
        tags: (c.tags || []).join(", "),
        mandate_sectors: (c.mandate_sectors || []).join(", "),
        mandate_geographies: (c.mandate_geographies || []).join(", "),
        mandate_ebitda_min: c.mandate_ebitda_min ?? "",
        mandate_ebitda_max: c.mandate_ebitda_max ?? "",
        ticket_size_min: c.ticket_size_min ?? "",
        ticket_size_max: c.ticket_size_max ?? "",
        past_acquisitions: (c.past_acquisitions || []).join(", "),
        notes: c.notes || "",
    });
    const [saving, setSaving] = useState(false);
    const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

    // Re-sync when dialog reopens with fresh contact
    useEffect(() => {
        if (!open) return;
        setForm({
            name: c.name || "",
            type: c.type || "buyer",
            email: c.email || "",
            phone: c.phone || "",
            company: c.company || "",
            role: c.role || "",
            relationship_owner: c.relationship_owner || "",
            tags: (c.tags || []).join(", "),
            mandate_sectors: (c.mandate_sectors || []).join(", "),
            mandate_geographies: (c.mandate_geographies || []).join(", "),
            mandate_ebitda_min: c.mandate_ebitda_min ?? "",
            mandate_ebitda_max: c.mandate_ebitda_max ?? "",
            ticket_size_min: c.ticket_size_min ?? "",
            ticket_size_max: c.ticket_size_max ?? "",
            past_acquisitions: (c.past_acquisitions || []).join(", "),
            notes: c.notes || "",
        });
    }, [open]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                name: form.name,
                type: form.type,
                email: form.email || null,
                phone: form.phone || null,
                company: form.company || null,
                role: form.role || null,
                relationship_owner: form.relationship_owner || null,
                tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
                notes: form.notes || null,
            };
            if (form.type === "buyer") {
                payload.mandate_sectors = form.mandate_sectors ? form.mandate_sectors.split(",").map((s) => s.trim()).filter(Boolean) : [];
                payload.mandate_geographies = form.mandate_geographies ? form.mandate_geographies.split(",").map((s) => s.trim()).filter(Boolean) : [];
                payload.mandate_ebitda_min = form.mandate_ebitda_min !== "" ? parseFloat(form.mandate_ebitda_min) : null;
                payload.mandate_ebitda_max = form.mandate_ebitda_max !== "" ? parseFloat(form.mandate_ebitda_max) : null;
                payload.ticket_size_min = form.ticket_size_min !== "" ? parseFloat(form.ticket_size_min) : null;
                payload.ticket_size_max = form.ticket_size_max !== "" ? parseFloat(form.ticket_size_max) : null;
                payload.past_acquisitions = form.past_acquisitions ? form.past_acquisitions.split(",").map((s) => s.trim()).filter(Boolean) : [];
            }
            await api.patch(`/contacts/${c.id}`, payload);
            toast.success("Contact updated");
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-contact-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Pencil className="w-4 h-4 text-gold" /> Edit contact
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name *</Label>
                            <Input required value={form.name} onChange={setF("name")} data-testid="edit-contact-name" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Type</Label>
                            <Select value={form.type} onValueChange={setF("type")}>
                                <SelectTrigger data-testid="edit-contact-type"><SelectValue /></SelectTrigger>
                                <SelectContent>{CONTACT_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                            <Input type="email" value={form.email} onChange={setF("email")} data-testid="edit-contact-email" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
                            <Input value={form.phone} onChange={setF("phone")} data-testid="edit-contact-phone" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Company</Label>
                            <Input value={form.company} onChange={setF("company")} data-testid="edit-contact-company" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Role</Label>
                            <Input value={form.role} onChange={setF("role")} data-testid="edit-contact-role" />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Relationship owner</Label>
                        <Input value={form.relationship_owner} onChange={setF("relationship_owner")} placeholder="Broker name" data-testid="edit-contact-rel-owner" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tags (comma separated)</Label>
                        <Input value={form.tags} onChange={setF("tags")} placeholder="PE, Family Office, Strategic Buyer" data-testid="edit-contact-tags" />
                    </div>

                    {form.type === "buyer" && (
                        <div className="pt-2 mt-2 border-t border-border space-y-3">
                            <div className="text-xs uppercase tracking-wider text-foreground font-medium">Buyer mandate</div>
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Sectors (comma separated)</Label>
                                <Input value={form.mandate_sectors} onChange={setF("mandate_sectors")} placeholder="Healthcare, SaaS, Industrials" data-testid="edit-contact-sectors" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Geographies</Label>
                                <Input value={form.mandate_geographies} onChange={setF("mandate_geographies")} placeholder="UK, Germany, Nordics" data-testid="edit-contact-geos" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">EBITDA min</Label>
                                    <Input type="number" className="font-mono" value={form.mandate_ebitda_min} onChange={setF("mandate_ebitda_min")} data-testid="edit-contact-ebitda-min" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">EBITDA max</Label>
                                    <Input type="number" className="font-mono" value={form.mandate_ebitda_max} onChange={setF("mandate_ebitda_max")} data-testid="edit-contact-ebitda-max" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Ticket size min</Label>
                                    <Input type="number" className="font-mono" value={form.ticket_size_min} onChange={setF("ticket_size_min")} data-testid="edit-contact-ticket-min" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Ticket size max</Label>
                                    <Input type="number" className="font-mono" value={form.ticket_size_max} onChange={setF("ticket_size_max")} data-testid="edit-contact-ticket-max" />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Past acquisitions (comma separated)</Label>
                                <Input value={form.past_acquisitions} onChange={setF("past_acquisitions")} placeholder="Acme Corp, Beta Ltd" data-testid="edit-contact-past-acq" />
                            </div>
                        </div>
                    )}

                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
                        <Textarea rows={2} value={form.notes} onChange={setF("notes")} data-testid="edit-contact-notes" />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-contact-cancel">
                            Cancel
                        </Button>
                        <Button type="submit" disabled={saving} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="edit-contact-save-btn">
                            {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Intelligence Tab + helpers
// ─────────────────────────────────────────────────────────────────────────────

function MiniStat({ label, value, mono, testid }) {
    return (
        <div className="border border-border rounded-md bg-white p-3" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`text-lg font-bold ${mono ? "font-mono text-navy" : "font-display"} mt-0.5`}>{value ?? "—"}</div>
        </div>
    );
}

function AcqFreqBadge({ value }) {
    return (
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border font-medium ${ACQ_FREQ_BADGE[value] || "bg-neutral-100 text-neutral-600 border-neutral-300"}`}>
            {ACQ_FREQ_LABEL[value] || value}
        </span>
    );
}

function RelStrengthBadge({ value }) {
    return (
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border font-medium ${REL_STRENGTH_BADGE[value] || "bg-neutral-100 text-neutral-600 border-neutral-300"}`} data-testid="rel-strength-badge">
            {REL_STRENGTH_LABEL[value] || value}
        </span>
    );
}

function IntelligenceTab({ contact, intel, loading, onEdit }) {
    if (loading) {
        return <div className="text-sm text-muted-foreground p-4">Loading intelligence data…</div>;
    }
    const ig = intel?.intelligence || {};
    const stats = intel?.stats || {};

    return (
        <div className="space-y-4" data-testid="intelligence-tab">
            {/* Mini stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MiniStat label="Deals tracked" value={stats.deals_tracked ?? 0} testid="intel-stat-deals-tracked" />
                <MiniStat label="IOIs submitted" value={stats.iois_submitted ?? 0} testid="intel-stat-iois" />
                <MiniStat label="Mandate coverage" value={`${stats.mandate_coverage_score ?? 0}%`} testid="intel-stat-coverage" />
                <MiniStat label="Avg IOI value" value={fmtMoney(stats.avg_ioi_value ?? 0, "GBP")} mono testid="intel-stat-avg-ioi" />
            </div>

            <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={onEdit} data-testid="edit-intel-btn">
                    <Brain className="w-3.5 h-3.5 mr-1.5 text-gold" /> Edit intelligence
                </Button>
            </div>

            {/* Section 1: Acquisition Track Record */}
            <div className="border border-border rounded-md bg-white p-5">
                <h3 className="font-display text-sm font-bold tracking-tight uppercase text-muted-foreground mb-3 flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5 text-gold" /> Acquisition Track Record
                </h3>
                {ig.acquisition_frequency && (
                    <div className="py-1.5">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Activity level</div>
                        <AcqFreqBadge value={ig.acquisition_frequency} />
                    </div>
                )}
                <KV label="Avg EBITDA acquired" value={ig.avg_ebitda_acquired != null ? fmtMoney(ig.avg_ebitda_acquired, "GBP") : "—"} mono />
                <KV label="Avg EV acquired" value={ig.avg_ev_acquired != null ? fmtMoney(ig.avg_ev_acquired, "GBP") : "—"} mono />
                <KV label="Last acquisition" value={ig.last_acquisition_date ? new Date(ig.last_acquisition_date).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "—"} />
                {ig.preferred_deal_type?.length > 0 && (
                    <div className="py-1.5">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Preferred deal types</div>
                        <div className="flex flex-wrap gap-1">
                            {ig.preferred_deal_type.map((t) => (
                                <span key={t} className="text-[10px] px-2 py-0.5 bg-muted/40 border border-border rounded-sm uppercase tracking-wide font-mono">
                                    {DEAL_TYPE_DISPLAY[t] || t}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                {ig.acquisition_history?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border/60">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Named acquisitions</div>
                        <ul className="space-y-1">
                            {ig.acquisition_history.map((a, i) => (
                                <li key={i} className="font-mono text-xs text-foreground border-l-2 border-gold/40 pl-2">{a}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {!ig.acquisition_frequency && ig.avg_ebitda_acquired == null && ig.avg_ev_acquired == null && !ig.last_acquisition_date && !ig.acquisition_history?.length && (
                    <p className="text-xs text-muted-foreground italic mt-1">No acquisition track record recorded yet.</p>
                )}
            </div>

            {/* Section 2: Deal Preferences */}
            <div className="border border-border rounded-md bg-white p-5">
                <h3 className="font-display text-sm font-bold tracking-tight uppercase text-muted-foreground mb-3 flex items-center gap-1.5">
                    <Target className="w-3.5 h-3.5 text-gold" /> Deal Preferences
                </h3>
                {ig.preferred_deal_type?.length > 0 ? (
                    <div className="py-1.5">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Deal structures</div>
                        <div className="flex flex-wrap gap-1">
                            {ig.preferred_deal_type.map((t) => (
                                <span key={t} className="text-[10px] px-2 py-0.5 bg-gold/10 text-gold border border-gold/30 rounded-sm font-mono">
                                    {DEAL_TYPE_DISPLAY[t] || t}
                                </span>
                            ))}
                        </div>
                    </div>
                ) : (
                    <p className="text-xs text-muted-foreground italic mb-2">No deal type preferences set.</p>
                )}
                <RangeRow label="EBITDA mandate" lo={contact.mandate_ebitda_min} hi={contact.mandate_ebitda_max} />
                <RangeRow label="Ticket size" lo={contact.ticket_size_min} hi={contact.ticket_size_max} accent />
                <CriteriaRow icon={<Briefcase className="w-3.5 h-3.5" />} label="Sectors" values={contact.mandate_sectors} />
                <CriteriaRow icon={<MapPin className="w-3.5 h-3.5" />} label="Geographies" values={contact.mandate_geographies} />
            </div>

            {/* Section 3: Relationship */}
            <div className="border border-border rounded-md bg-white p-5">
                <h3 className="font-display text-sm font-bold tracking-tight uppercase text-muted-foreground mb-3 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-gold" /> Relationship
                </h3>
                {ig.relationship_strength && (
                    <div className="py-1.5">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Relationship strength</div>
                        <RelStrengthBadge value={ig.relationship_strength} />
                    </div>
                )}
                <KV label="Relationship owner" value={contact.relationship_owner || "—"} />
                <KV label="PE-backed" value={ig.pe_backed === true ? "Yes" : ig.pe_backed === false ? "No" : "—"} />
                {ig.pe_backed && ig.pe_fund_name && <KV label="Fund name" value={ig.pe_fund_name} mono />}
                {ig.known_advisers?.length > 0 && (
                    <div className="py-1.5">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Known advisers</div>
                        <div className="flex flex-wrap gap-1">
                            {ig.known_advisers.map((a, i) => (
                                <span key={i} className="text-xs px-2 py-0.5 bg-muted/40 border border-border rounded-sm">{a}</span>
                            ))}
                        </div>
                    </div>
                )}
                {ig.intelligence_notes && (
                    <div className="mt-3 pt-3 border-t border-border/60">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Intelligence notes</div>
                        <p className="text-xs whitespace-pre-wrap text-foreground/90 leading-relaxed">{ig.intelligence_notes}</p>
                    </div>
                )}
                {!ig.relationship_strength && ig.pe_backed == null && !ig.known_advisers?.length && !ig.intelligence_notes && (
                    <p className="text-xs text-muted-foreground italic mt-1">No relationship intelligence recorded yet.</p>
                )}
            </div>
        </div>
    );
}

function IntelligenceEditDialog({ contact, intel, open, onOpenChange, onDone }) {
    const ig = intel?.intelligence || {};
    const [form, setForm] = useState({
        acquisition_history: (ig.acquisition_history || []).join("\n"),
        acquisition_frequency: ig.acquisition_frequency || "",
        avg_ebitda_acquired: ig.avg_ebitda_acquired ?? "",
        avg_ev_acquired: ig.avg_ev_acquired ?? "",
        last_acquisition_date: ig.last_acquisition_date || "",
        preferred_deal_type: ig.preferred_deal_type || [],
        pe_backed: ig.pe_backed || false,
        pe_fund_name: ig.pe_fund_name || "",
        known_advisers: (ig.known_advisers || []).join("\n"),
        relationship_strength: ig.relationship_strength || "",
        intelligence_notes: ig.intelligence_notes || "",
    });
    const [saving, setSaving] = useState(false);

    // Re-sync form when dialog opens with fresh intel
    useEffect(() => {
        if (!open) return;
        const latest = intel?.intelligence || {};
        setForm({
            acquisition_history: (latest.acquisition_history || []).join("\n"),
            acquisition_frequency: latest.acquisition_frequency || "",
            avg_ebitda_acquired: latest.avg_ebitda_acquired ?? "",
            avg_ev_acquired: latest.avg_ev_acquired ?? "",
            last_acquisition_date: latest.last_acquisition_date || "",
            preferred_deal_type: latest.preferred_deal_type || [],
            pe_backed: latest.pe_backed || false,
            pe_fund_name: latest.pe_fund_name || "",
            known_advisers: (latest.known_advisers || []).join("\n"),
            relationship_strength: latest.relationship_strength || "",
            intelligence_notes: latest.intelligence_notes || "",
        });
    }, [open]);

    const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
    const toggleDealType = (t) => setForm((f) => ({
        ...f,
        preferred_deal_type: f.preferred_deal_type.includes(t)
            ? f.preferred_deal_type.filter((x) => x !== t)
            : [...f.preferred_deal_type, t],
    }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                acquisition_history: form.acquisition_history.split("\n").map((s) => s.trim()).filter(Boolean),
                acquisition_frequency: form.acquisition_frequency.trim() || null,
                avg_ebitda_acquired: form.avg_ebitda_acquired !== "" ? Number(form.avg_ebitda_acquired) : null,
                avg_ev_acquired: form.avg_ev_acquired !== "" ? Number(form.avg_ev_acquired) : null,
                last_acquisition_date: form.last_acquisition_date || null,
                preferred_deal_type: form.preferred_deal_type,
                pe_backed: form.pe_backed,
                pe_fund_name: form.pe_fund_name.trim() || null,
                known_advisers: form.known_advisers.split("\n").map((s) => s.trim()).filter(Boolean),
                relationship_strength: form.relationship_strength.trim() || null,
                intelligence_notes: form.intelligence_notes.trim() || null,
            };
            await api.patch(`/contacts/${contact.id}`, payload);
            toast.success("Intelligence updated");
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="intel-edit-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Brain className="w-4 h-4 text-gold" /> Acquisition Intelligence · {contact.name}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-5">
                    {/* Acquisition Track Record */}
                    <div className="space-y-3">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold border-b border-border/60 pb-1.5">Acquisition Track Record</div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Activity level</Label>
                                <Select value={form.acquisition_frequency} onValueChange={setF("acquisition_frequency")}>
                                    <SelectTrigger data-testid="intel-freq-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value=" ">Not set</SelectItem>
                                        <SelectItem value="very_active">Very active (3+ /yr)</SelectItem>
                                        <SelectItem value="active">Active (1–2 /yr)</SelectItem>
                                        <SelectItem value="occasional">Occasional</SelectItem>
                                        <SelectItem value="rare">Rare</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Last acquisition date</Label>
                                <input type="date" className="w-full h-9 border border-border rounded-md px-2 text-sm" value={form.last_acquisition_date} onChange={setF("last_acquisition_date")} data-testid="intel-last-acq-date" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Avg EBITDA acquired (£)</Label>
                                <Input type="number" value={form.avg_ebitda_acquired} onChange={setF("avg_ebitda_acquired")} placeholder="e.g. 750000" data-testid="intel-avg-ebitda" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Avg EV acquired (£)</Label>
                                <Input type="number" value={form.avg_ev_acquired} onChange={setF("avg_ev_acquired")} placeholder="e.g. 4500000" data-testid="intel-avg-ev" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Acquisition history (one company per line)</Label>
                            <textarea rows={3} className="w-full border border-border rounded-md p-2 text-sm resize-none" value={form.acquisition_history} onChange={setF("acquisition_history")} placeholder={"Acme Corp\nBeta Ltd"} data-testid="intel-acq-history" />
                        </div>
                    </div>

                    {/* Deal Preferences */}
                    <div className="space-y-3">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold border-b border-border/60 pb-1.5">Deal Preferences</div>
                        <div className="space-y-1.5">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">Preferred deal types</div>
                            <div className="flex flex-wrap gap-2">
                                {DEAL_TYPE_OPTS.map((t) => (
                                    <label key={t} className={`flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-md cursor-pointer transition-colors select-none ${form.preferred_deal_type.includes(t) ? "bg-gold/15 border-gold/60 text-gold font-medium" : "border-border text-muted-foreground hover:bg-muted/40"}`} data-testid={`intel-deal-type-${t}`}>
                                        <input type="checkbox" className="sr-only" checked={form.preferred_deal_type.includes(t)} onChange={() => toggleDealType(t)} />
                                        {DEAL_TYPE_DISPLAY[t]}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Relationship */}
                    <div className="space-y-3">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold border-b border-border/60 pb-1.5">Relationship</div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Relationship strength</Label>
                                <Select value={form.relationship_strength} onValueChange={setF("relationship_strength")}>
                                    <SelectTrigger data-testid="intel-rel-strength-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value=" ">Not set</SelectItem>
                                        <SelectItem value="cold">Cold</SelectItem>
                                        <SelectItem value="warm">Warm</SelectItem>
                                        <SelectItem value="strong">Strong</SelectItem>
                                        <SelectItem value="strategic_partner">Strategic Partner</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-end pb-1">
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input type="checkbox" className="accent-navy w-4 h-4" checked={form.pe_backed} onChange={(e) => setForm((f) => ({ ...f, pe_backed: e.target.checked }))} data-testid="intel-pe-backed-check" />
                                    PE-backed buyer
                                </label>
                            </div>
                        </div>
                        {form.pe_backed && (
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Fund name</Label>
                                <Input value={form.pe_fund_name} onChange={setF("pe_fund_name")} placeholder="e.g. Alpha Capital" data-testid="intel-pe-fund-name" />
                            </div>
                        )}
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Known advisers (one per line)</Label>
                            <textarea rows={2} className="w-full border border-border rounded-md p-2 text-sm resize-none" value={form.known_advisers} onChange={setF("known_advisers")} placeholder={"Grant Thornton\nClifford Chance"} data-testid="intel-known-advisers" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Intelligence notes</Label>
                            <textarea rows={3} className="w-full border border-border rounded-md p-2 text-sm resize-none" value={form.intelligence_notes} onChange={setF("intelligence_notes")} placeholder="Key insights about this buyer…" data-testid="intel-notes-textarea" />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="submit" disabled={saving} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="intel-save-btn">
                            {saving ? "Saving…" : "Save intelligence"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
